import { AllWorkerAgreementGridDto } from 'src/app/models/dtos/AllWorkerAgreementGridDto';
import { MultipleWorkerAgreementsActionSelectedWorkersDto } from 'src/app/models/dtos/multiple-worker-agreements-action-selected-workers-dto';
import { MultipleWorkerAgreementsActionSelectedWorkerAgreementDto } from 'src/app/models/dtos/multiple-worker-agreements-action-selected-worker-agreement-dto';
import { AgreementTypeEnum } from 'src/app/models/enums/worker-agreement-type-enum';
import { WorkerAgreementStatusEnum } from 'src/app/models/enums/worker-agreement-status-enum';

/**
 * Transforms selected rows into deduplicated DTO array (deduplicates by WorkerId).
 */
export function selectWithoutDuplicates(selected: AllWorkerAgreementGridDto[]): MultipleWorkerAgreementsActionSelectedWorkersDto[] {
  return selected
    .map((s) => ({
      Id: s.WorkerId,
      Name: s.FirstName + ' ' + s.LastName,
      AgreementId: s.AgreementId,
      WorkerFormId: s.WorkerFormId,
      CompanyId: s.CompanyId,
      CompanyName: s.Company,
    }))
    .filter((v, i, a) => a.findIndex((t) => t.Id === v.Id) === i);
}

/**
 * Transforms selected rows into deduplicated agreement DTOs.
 */
export function selectWorkerAgreementsWithoutDuplicates(selected: AllWorkerAgreementGridDto[]): MultipleWorkerAgreementsActionSelectedWorkerAgreementDto[] {
  return selected.map((s) => ({
    Id: s.AgreementId,
    HasEmploymentConditionsConfirmation: s.HasEmploymentConditionsConfirmation,
  } as MultipleWorkerAgreementsActionSelectedWorkerAgreementDto));
}

/**
 * Determines if multiple worker agreements can be extended based on selection.
 * Pure function for reusability and testability.
 */
export function getExtensionOnMultipleAgreementsAvailability(
  isAllSelected: boolean,
  selected: AllWorkerAgreementGridDto[],
  multipleActionsConfig: any
): { enabled: boolean; reasonKey: string } {
  if (isAllSelected) {
    const isReady = multipleActionsConfig?.CanExtendAgreements;

    if (isReady === true) {
      return { enabled: true, reasonKey: '' };
    }

    if (isReady === false) {
      return {
        enabled: false,
        reasonKey: 'ExtendMultipleAgreementsUnavailableByFilterResult',
      };
    }

    return {
      enabled: false,
      reasonKey: 'ActionAvailabilityPending',
    };
  }

  if (!selected.length) {
    return {
      enabled: false,
      reasonKey: 'ExtendMultipleAgreementsRequiresSelection',
    };
  }

  if (!hasSameEmploymentTypeCompanyAndEmployerObject(selected)) {
    return {
      enabled: false,
      reasonKey: 'ExtendMultipleAgreementsRequiresSameEmploymentTypeCompanyAndEmployerObject',
    };
  }

  if (selected.some((s) => s.AgreementTypeId === AgreementTypeEnum.Permanent)) {
    return {
      enabled: false,
      reasonKey: 'ExtendMultipleAgreementsPermanentForbidden',
    };
  }

  if (selected.some((s) => s.AgreementStatusId !== WorkerAgreementStatusEnum.Accepted && s.AgreementStatusId !== WorkerAgreementStatusEnum.Active)) {
    return {
      enabled: false,
      reasonKey: 'ExtendMultipleAgreementsRequiresAcceptedOrActiveStatus',
    };
  }

  return { enabled: true, reasonKey: '' };
}

/**
 * Checks if all selected agreements have the same employment type, company, and employer object.
 * Used internally by getExtensionOnMultipleAgreementsAvailability.
 */
function hasSameEmploymentTypeCompanyAndEmployerObject(selected: AllWorkerAgreementGridDto[]): boolean {
  if (selected.length === 0) {
    return false;
  }
  const first = selected[0];
  return !!first.CompanyId &&
    selected.every((item) =>
      item.EmploymentTypeId === first.EmploymentTypeId &&
      item.CompanyId === first.CompanyId &&
      item.EmployerObjectId === first.EmployerObjectId
    );
}
